import { useState, useEffect } from 'react'
import { Event, User, InvitedTeamMembers } from '../redux/event/manageEvents/types'
import { validateString, validateNumber, validateURL } from '../utils/validationUtils'
import { categorySelectOptions } from '../constant/Event'

export type FormData = {
  title: string
  venue: string
  details: string
  startDate: Date | null
  endDate: Date | null
  category: string
  type: string
  importance: string
  googleMeetLink: string
  postSurveyURL: string
  estimatedBudget: string
  targetCompliance: string
  imageFilename: string
  createdBy?: User
  teamMembers: InvitedTeamMembers[]
}

const initialFormData: FormData = {
  title: '',
  venue: '',
  details: '',
  startDate: null,
  endDate: null,
  category: '',
  type: '',
  importance: '',
  googleMeetLink: '',
  postSurveyURL: '',
  estimatedBudget: '',
  targetCompliance: '',
  imageFilename: '',
  teamMembers: [],
}

const useEventForm = (eventDetails?: Event) => {
  const [formData, setFormData] = useState<FormData>(eventDetails ? ({} as FormData) : initialFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [imageFile, setImageFile] = useState<File | undefined>()

  useEffect(() => {
    if (eventDetails) {
      setFormData({
        title: eventDetails.title || '',
        venue: eventDetails.venue || '',
        details: eventDetails.details || '',
        startDate: eventDetails.startDate ? new Date(eventDetails.startDate) : null,
        endDate: eventDetails.endDate ? new Date(eventDetails.endDate) : null,
        category: eventDetails.category || '',
        type: eventDetails.type || '',
        importance: eventDetails.importance || '',
        googleMeetLink: eventDetails.googleMeetLink || '',
        postSurveyURL: eventDetails.postSurveyURL || '',
        estimatedBudget: eventDetails.estimatedBudget || '',
        targetCompliance: eventDetails.targetCompliance || '',
        imageFilename: eventDetails.imageFilename || '',
        createdBy: eventDetails.createdBy,
        teamMembers: eventDetails.invitedTeamMembers || [],
      })
    }
  }, [eventDetails])

  const hasFormChanges = (): boolean => {
    if (!eventDetails) return false

    const compareDates = (formDate: Date | null, eventDate: Date | string | null): boolean => {
      if (!formDate && !eventDate) return false
      if (!formDate || !eventDate) return true
      const eventDateTime =
        typeof eventDate === 'string' ? new Date(eventDate).getTime() : eventDate.getTime()
      return formDate.getTime() !== eventDateTime
    }

    return (
      formData.title !== (eventDetails.title || '') ||
      formData.venue !== (eventDetails.venue || '') ||
      formData.details !== (eventDetails.details || '') ||
      compareDates(formData.startDate, eventDetails.startDate) ||
      compareDates(formData.endDate, eventDetails.endDate) ||
      formData.category !== (eventDetails.category || '') ||
      formData.type !== (eventDetails.type || '') ||
      formData.importance !== (eventDetails.importance || '') ||
      formData.googleMeetLink !== (eventDetails.googleMeetLink || '') ||
      formData.postSurveyURL !== (eventDetails.postSurveyURL || '') ||
      formData.estimatedBudget !== (eventDetails.estimatedBudget || '') ||
      formData.targetCompliance !== (eventDetails.targetCompliance || '') ||
      formData.imageFilename !== (eventDetails.imageFilename || '') ||
      JSON.stringify(formData.teamMembers) !== JSON.stringify(eventDetails.invitedTeamMembers || [])
    )
  }

  const validateField = (name: keyof FormData, value: FormData[keyof FormData]): string => {
    const category = formData.category ? categorySelectOptions[formData.category] : ''
    const stringValue = value?.toString() || ''

    switch (name) {
      case 'title':
        return validateString(stringValue, 'Event title', { required: true })
      case 'details':
        return validateString(stringValue, 'Event details', { required: true })
      case 'category':
        return validateString(stringValue, 'Event category', { required: true })
      case 'venue':
        return validateString(stringValue, 'Event venue', { required: true })
      case 'type':
        if (category && category.eventTypeSelectOptions.length > 0) {
          return validateString(stringValue, 'Event type', { required: true })
        }
        return ''
      case 'importance':
        return validateString(stringValue, 'Event importance', { required: true })
      case 'estimatedBudget':
        return validateNumber(stringValue, 'Estimated budget', { positive: true })
      case 'targetCompliance':
        return validateNumber(stringValue, 'Target compliance', {
          required: true,
          positive: true,
          min: 1,
          max: 100,
        })
      case 'startDate': {
        if (!value || value === null) {
          return 'Event Start Date is required.'
        }
        const startDate = value as Date
        if (!eventDetails?.id && startDate < new Date()) {
          return 'Invalid start date and time. Please select a date and time that occur after the current date and time.'
        } else if (eventDetails?.id) {
          if (
            eventDetails?.startDate &&
            startDate.getTime() !== new Date(eventDetails.startDate).getTime() &&
            startDate < new Date()
          ) {
            return 'Invalid start date and time. Please select a date and time that occur after the current date and time.'
          }
        }
        return ''
      }
      case 'endDate': {
        if (!value || value === null) {
          return 'Event End Date is required.'
        }
        const endDate = value as Date
        if (formData.startDate && endDate <= formData.startDate) {
          return 'Invalid end date and time. Please select an end date and time that occur after the start date and time.'
        }
        return ''
      }
      case 'googleMeetLink':
        return stringValue.trim() !== ''
          ? validateURL(stringValue, 'Google meet link', {
              exampleUrl: 'https://meet.google.com/abc-defg-hij',
            })
          : ''
      case 'postSurveyURL':
        return stringValue.trim() !== '' ? validateURL(stringValue, 'Post Survey link') : ''
      default:
        return ''
    }
  }

  const handleChange = (name: keyof FormData, value: any) => {
    setFormData((prevState) => ({ ...prevState, [name]: value }))
    const error = validateField(name, value)
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }))

    if (name === 'category') {
      const categoryOptions = categorySelectOptions[value]?.eventTypeSelectOptions || []

      setFormData((prevState) => ({
        ...prevState,
        type: categoryOptions.length === 0 ? '' : prevState.type,
      }))

      setErrors((prevErrors) => ({
        ...prevErrors,
        type: categoryOptions.length === 0 ? '' : prevErrors.type,
      }))
    }
  }

  console.log(formData.type)

  const validateStep = (step: number): Record<string, string> => {
    const stepErrors: Record<string, string> = {}
    const fieldsToValidate =
      [
        ['title', 'details', 'category', 'type', 'estimatedBudget', 'targetCompliance'],
        ['startDate', 'endDate', 'importance', 'venue', 'googleMeetLink', 'postSurveyURL'],
      ][step] || []

    fieldsToValidate.forEach((field) => {
      const error = validateField(field as keyof FormData, formData[field as keyof FormData])
      if (error) {
        stepErrors[field] = error
      }
    })

    return stepErrors
  }

  const isFormValid = () => {
    const allErrors = { ...validateStep(0), ...validateStep(1) }
    return Object.values(allErrors).every((error) => !error)
  }

  return {
    formData,
    errors,
    setErrors,
    imageFile,
    setImageFile,
    handleChange,
    validateStep,
    isFormValid,
    hasFormChanges,
  }
}

export default useEventForm
